import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import './index.css';
import img1 from '../../../src/images/left-white.svg'
import img2 from '../../../src/images/7-days-icon.svg'
import img5 from '../../../src/images/key-insights-icon.svg'
import img6 from '../../../src/images/recommendation-icon.svg'
import img8 from '../../../src/images/home2.svg';
import img9 from '../../../src/images/Icon (5).svg';
import img10 from '../../../src/images/notification.svg';
import img11 from '../../../src/images/Calendar3.svg';
import img12 from '../../../src/images/Icon (6).svg';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    BarChart,
    Bar,
} from 'recharts';
import config from '../../config/config';


function EventsFrequency() {
    const navigate = useNavigate();
    const location = useLocation();
    const { patientName, patientAge, patientCondition, patientId } = location.state || {};

    const [patients, setPatients] = useState([]);
    const [selectedRange, setSelectedRange] = useState(7); // Default range is 7 days
    const [eventsData, setEventsData] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(null);   
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const backendURL = `${config.backendServerHost}`;

    // Fetch patients list
useEffect(() => {
    fetch(`${backendURL}/patients/api/patients`)
        .then((res) => res.json())
        .then((data) => {
            setPatients(data); // Store all patients
            const foundPatient = data.find(patient => patient.name === patientName); // Find patient by name
            if (foundPatient) {
                setSelectedPatient(foundPatient); // Set the selected patient
            } else {
                console.error(`Patient with name "${patientName}" not found.`);
            }
        })
        .catch((error) => console.error('Error fetching patients:', error));
}, [backendURL, patientName]);


// Fetch patient-specific data when a patient is selected
  useEffect(() => {
    if (!selectedPatient || !selectedPatient.id) return;

    setLoading(true);

    fetch(`${backendURL}/patients/api/events-frequency/${selectedPatient.id}`)
      .then((res) => res.json())
      .then((data) => {
        setEventsData(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [selectedPatient]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!eventsData) return <div>No data available</div>;



const EventsFrequencyChart = ({ patientId }) => {
    const [eventsData, setEventsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!patientId) return;

        setLoading(true);
        fetch(`${backendURL}/patients/api/events-frequency/${patientId}`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to fetch event data');
                }
                return res.json();
            })
            .then((data) => {
                // Sort dailyEvents by date to ensure proper order
                const sortedDailyEvents = [...data.dailyEvents].sort(
                    (a, b) => new Date(a.date) - new Date(b.date)
                );
                setEventsData({ ...data, dailyEvents: sortedDailyEvents });
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, [patientId]);

    if (loading) {
        return <div>Loading events data...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!eventsData) {
        return <div>No events data available</div>;
    }

    const { hyperEvents, hypoEvents, dailyEvents, startDate } = eventsData;

    // Format X-Axis ticks as month/day
    const formatXAxis = (tick) => {
        const date = new Date(tick);
        return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    return (
        <div className="events-frequency">
            <h2>Events Frequency</h2>
            <p>Data starting from: {new Date(startDate).toLocaleDateString()}</p>

            <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dailyEvents}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date" // Use the `date` field from dailyEvents
                            tickFormatter={formatXAxis} // Format dates on the X-axis
                        />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="hyper" fill="#f44336" name="Hyper Events" />
                        <Bar dataKey="hypo" fill="#3f51b5" name="Hypo Events" />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="event-summary">
                <p>
                    <strong>Total Hyper Events:</strong> {hyperEvents}
                </p>
                <p>
                    <strong>Total Hypo Events:</strong> {hypoEvents}
                </p>
            </div>
        </div>
    );
};

const DailyInsulinPattern = ({ patientId }) => {
    const [insulinData, setInsulinData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!patientId) return;

        setLoading(true);
        fetch(`${backendURL}/patients/api/events-frequency/${patientId}`)
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to fetch event data');
                }
                return res.json();
            })
            .then((data) => {
                const { dailyEvents } = data;

                // Map the daily events to total insulin data
                const formattedData = dailyEvents.map((event) => ({
                    date: event.date,
                    totalInsulin: event.hyper + event.hypo, // Assuming hyper and hypo are proxies for insulin usage
                }));

                // Sort data chronologically
                formattedData.sort((a, b) => new Date(a.date) - new Date(b.date));

                setInsulinData(formattedData);
                setLoading(false);
            })
            .catch((err) => {
                setError(err.message);
                setLoading(false);
            });
    }, [patientId]);

    if (loading) {
        return <div>Loading insulin data...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    if (!insulinData) {
        return <div>No insulin data available</div>;
    }

    // Format X-Axis ticks as month/day
    const formatXAxis = (tick) => {
        const date = new Date(tick);
        return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    return (
        <div className="daily-insulin-pattern">
            <h2>Daily Insulin Pattern (Last 7 Days)</h2>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={insulinData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="date"
                        tickFormatter={formatXAxis} // Format dates on the X-axis
                    />
                    <YAxis />
                    <Tooltip />
                    <Line
                        type="monotone"
                        dataKey="totalInsulin"
                        stroke="#8884d8"
                        name="Total Daily Insulin"
                        dot={true}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

  const handleRangeChange = (event) => {
        setSelectedRange(parseInt(event.target.value, 10));
    };

    const calculateDateRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - selectedRange);
        const options = { month: 'short', day: 'numeric' };

        return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
    };

    const handlePatientChange = (e) => {
        const patientId = e.target.value;
        const patient = patients.find((p) => p.id === patientId);
        setSelectedPatient(patient);
    };

    const navigateTo = (path, state = {}) => {
    navigate(path, { state });
    };

    return (
        <div className="glucose-main-cantainer">
            <div className='glucose_container events-container'>
                <div className='glucose-navbar'>
                <div onClick={() => 
                    navigateTo('/patientinfo', {
                        state: { 
                            patientName: selectedPatient.name,
                            patientAge: selectedPatient.age,
                            patientCondition: selectedPatient.type,
                        },
                    })
            }>

                <img src={img1} alt="Back to Patient Info" />
                    </div>
                    <div><h4>Events Frequency</h4></div>
                    <div></div>
                </div>
                <div className='days-button'><span><img src={img2} alt="" />
                </span>
                {/*Date Range */}
                <div className="header">
                    <select onChange={handleRangeChange} value={selectedRange}>
                        <option value={7}>7 Days</option>
                        <option value={14}>14 Days</option>
                        <option value={30}>30 Days</option>
                    </select>
                </div>
            </div>
                <div className='date-heading'><h5>{calculateDateRange()}</h5></div>
                <div className='glucose_trend_cnt event-cnt'>
                    <div className='average_glucose time-in-range-cnt'>
                        <h3>17</h3>
                    </div>
                    <div className='average_glucose time-in-range-cnt'>
                        <h3>7</h3>
                    </div>
                </div>
                <div className='events_trend_text'>
                    <div><h3>Hyper Events</h3></div>
                    <div><h3>Hypo Events</h3></div>
                </div>

                {/* Events Bar Chart */}
                <div className="chart-section">
                    <EventsFrequencyChart patientId={selectedPatient.id} />
                </div>

            </div>
           

                
                
            {/* Events Line Chart */}
            <div className="chart-section">
                <DailyInsulinPattern patientId={selectedPatient.id} />
            </div>



                <div className='key-insights-heading'>
                    <h2>Key Insights</h2>
                </div>
                <div className='key_insights_cnt'>
                    <div>
                        <img src={img5} alt="" />
                    </div>
                    <div>
                        <h4>Key Insights</h4>
                        <p>Victor Borg, spent 62% of the week in the target glucose range (70-180 mg/dL)</p>
                    </div>
                </div>
                <div className='key_insights_cnt'>
                    <div>
                        <img src={img6} alt="" />
                    </div>
                    <div>
                        <h4>Recommendations </h4>
                        <p>Adjust evening insulin dose.</p>
                    </div>
                </div>
                <div className='key-insights-heading'>
                    <h2>Event Log</h2>
                </div>
                <div className='event_container1'>
                    <div>
                        <h2>Hypo Event</h2>
                        <h3>Nov 10 15:30</h3>
                        <h4>Blood glucose: 52 mg/dL</h4>
                    </div>
                    <div>
                        <h5>High</h5>
                    </div>
                </div>
                <div className='event_container1 event_container2'>
                    <div>
                        <h2>Hypo Event</h2>
                        <h3>Nov 10 15:30</h3>
                        <h4>Blood glucose: 52 mg/dL</h4>
                    </div>
                    <div>
                        <h5>Moderate</h5>
                    </div>
                </div>
                <div className='event_container1'>
                    <div>
                        <h2>Hypo Event</h2>
                        <h3>Nov 10 15:30</h3>
                        <h4>Blood glucose: 52 mg/dL</h4>
                    </div>
                    <div>
                        <h5>High</h5>
                    </div>
                </div>
                <div className='event_container1 event_container2'>
                    <div>
                        <h2>Hypo Event</h2>
                        <h3>Nov 10 15:30</h3>
                        <h4>Blood glucose: 52 mg/dL</h4>
                    </div>
                    <div>
                        <h5>Moderate</h5>
                    </div>
                </div>
            <div className="footer_container_wrapper">
                <div className="footer_container">
                    <div className="footer_icon" onClick={() => navigate('/')}>
                        <img src={img8} alt="Home Icon" />
                    </div>
                    <div className="footer_icon" onClick={() => navigate('/analysis')}>
                        <img src={img9} alt="Analytics Icon" />
                    </div>
                    <div className="footer_icon" onClick={() => navigate('/alerts')}>
                        <img src={img10} alt="Notification Icon" />
                    </div>
                    <div className="footer_icon" onClick={() => navigate('/appointments')}>
                        <img src={img11} alt="Calendar Icon" />
                    </div>
                    <div style={{ cursor: "pointer" }}>
                        <img src={img12} alt="" />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EventsFrequency;
